import { useLocation, useNavigate } from "react-router-dom";
import { Stack, styled, Typography } from "@mui/material";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { PartnerAfterImport } from "@/features/partner/components/partner-import/PartnerAfterImport";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import { FC, useEffect, useMemo, useState } from "react";
import { importPartnersApi, usePartnerList } from "@/api/services/main/partner";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";
import { black, divider, orange, state } from "@/theme/colors";
import { IconSave } from "@/assets/icons";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { rounded } from "@/theme/variables";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TPartnerImportRow } from "@/features/partner/types/partner-import";
import { sanitizeInput } from "@/utils/pieces";
import { PARTNER_IMPORT_MAX_ROWS } from "@/features/partner/constants/import";

type RowWarning = {
  row: number;
  messages: string[];
};

const normalizeName = (value?: string) => sanitizeInput(value ?? "").replace(/\s+/g, "");

// パートナー名の重複は仕様上許容されるため、保存は止めずに注意喚起のみ行う。
const checkImport = (partners: TPartnerImportRow[], existingPartnerNames: string[]) => {
  const warningsByRow: RowWarning[] = [];

  const nameToIndices: Record<string, number[]> = {};
  partners.forEach((item, idx) => {
    const name = normalizeName(item.partnerName);
    if (!nameToIndices[name]) {
      nameToIndices[name] = [];
    }
    nameToIndices[name].push(idx);
  });

  partners.forEach((item, idx) => {
    const rowNumber = idx + 1;
    const rowWarnings: string[] = [];
    const name = normalizeName(item.partnerName);

    const indices = nameToIndices[name];

    if (existingPartnerNames.includes(name)) {
      rowWarnings.push(`${rowNumber}行目の必須項目「パートナー名」（${item.partnerName}）が既に登録されています。`);
    }

    if (indices && indices.length > 1) {
      const rows = indices.map((i) => `${i + 1}行目`).join("、");
      rowWarnings.push(`${rows}の必須項目「パートナー名」（${item.partnerName}）が重複しています。`);
    }

    if (rowWarnings.length > 0) {
      warningsByRow.push({ row: rowNumber, messages: rowWarnings });
    }
  });

  const seen = new Set<string>();

  return warningsByRow.map((item) => ({
    row: item.row,
    messages: Array.from(new Set(item.messages)).filter((msg) => {
      if (seen.has(msg)) return false;
      seen.add(msg);
      return true;
    }),
  }));
};

export const PartnerImportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partner } = (location.state as { partner?: TPartnerImportRow[] }) || {};
  const totalRow = partner?.length ?? 0;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const backModal = useDialog();

  const nameFilters = useMemo(() => Array.from(new Set((partner ?? []).map((item) => normalizeName(item.partnerName)).filter(Boolean))), [partner]);

  const {
    data: partnerListData,
    isFetching,
    isError: isWarningCheckFailed,
  } = usePartnerList({ page: 1, perPage: PARTNER_IMPORT_MAX_ROWS, partnerNameNormalized: nameFilters }, nameFilters.length > 0);

  const warnings = useMemo(() => {
    if (isFetching || !partner) return [];
    const existingNames = (partnerListData?.data ?? []).map((item) => normalizeName(item.partnerName));
    return checkImport(partner, existingNames);
  }, [isFetching, partner, partnerListData]);

  useEffect(() => {
    if (!partner) {
      navigate(AppRoutes.partnerList);
    }
  }, [partner, navigate]);

  const handleSaveData = async () => {
    if (!partner || isFetching) return;
    setIsSubmitting(true);
    try {
      await importPartnersApi(partner);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST] });
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.partnerList);
    } catch (error) {
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
    }
    setIsSubmitting(false);
  };

  const TitleInnerBox: FC = () => {
    return (
      <Typography variant="sub16Semi" color={black[70]}>
        一括インポート：
        <Typography variant="head20Semi" color={black[30]}>
          {totalRow}
        </Typography>{" "}
        件
      </Typography>
    );
  };

  return (
    <>
      <PageWrapper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <TopPage
            title="パートナーインポート"
            subTitle="データから一括インポートされるパートナー情報です。内容に問題がなければ保存ボタンを押して取り込みを開始します。"
            enablePageBack={false}
          />
        </Stack>

        <Content>
          <InnerBox borderColor={divider.high} padding={3} mt={2} showDivider={false} title={<TitleInnerBox />}>
            <Stack
              flex={1}
              gap={3}
              sx={{
                "& .MuiTableContainer-root": {
                  paddingBottom: "10px",
                },
              }}
            >
              <PartnerAfterImport rows={partner ?? []} isLoading={isFetching} />
            </Stack>
            {warnings.length > 0 && (
              <Stack
                sx={{ mt: "32px", p: "8px", background: state.incomplete_secondary, color: orange[80], maxHeight: "160px", overflowY: "auto" }}
                borderRadius={rounded.xs}
              >
                {warnings.map((warn, index) => (
                  <Typography key={index} component="p" variant="cap12">
                    {warn.messages.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </Typography>
                ))}
                <Typography component="p" variant="cap12">
                  修正が必要な場合は修正後に再インポートしてください。
                </Typography>
              </Stack>
            )}
            {isWarningCheckFailed && (
              <Stack
                sx={{ mt: "32px", p: "8px", background: state.incomplete_secondary, color: orange[80], maxHeight: "160px", overflowY: "auto" }}
                borderRadius={rounded.xs}
              >
                <Typography component="p" variant="cap12">
                  パートナー名の重複確認ができませんでした。このまま保存できますが、重複の可能性があります。
                </Typography>
              </Stack>
            )}
          </InnerBox>
        </Content>

        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" size="sm" onClick={() => backModal.show()}>
              戻る
            </Button>
            <Stack direction="row" gap={3}>
              <Button
                variant="primary"
                size="sm"
                disabled={isSubmitting || isFetching}
                onClick={handleSaveData}
                startIcon={<IconSave />}
              >
                保存
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
      </PageWrapper>
      <ConfirmSaveModal open={backModal.open} onOk={() => navigate(AppRoutes.partnerList)} onClose={backModal.cancel}>
        <Typography component="p" variant="subtitle1">
          インポートが終了しておりませんが戻ってもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </>
  );
};

const PageWrapper = styled(Stack)`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const Content = styled(Stack)`
  flex: 1;
`;
